import { Typography, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IAttribute, IBreadcrumbsAndMenu } from "../../../types/global";
import CustomizedButton from "../../../components/Custom/CustomizedButton";
import CustomizedBreadcrumbs from "../../../components/Custom/CustomizedBreadcrumbs";
import { useModal } from "../../../hooks/use-modal";
import { GraphQLClient } from "graphql-request";
import { createGraphQLClientWithMiddleware } from "../../../services/graphqlClient";
import { useSnackbar } from "notistack";
import ConfirmationModal from "../../../components/UI/Modal/ConfirmationModal";
import {
  CustomerAttributeFindUniqueQuery,
  CustomerAttributesFindAllQuery,
  useCustomerAttributeCreateMutation,
  useCustomerAttributeDeleteMutation,
  useCustomerAttributeFindUniqueQuery,
  useCustomerAttributeUpdateMutation,
  useCustomerAttributesFindAllQuery,
} from "../../../generated/general";
import { useSearchParams } from "react-router-dom";
import { useState } from "react";
import AttributeTable from "../../../components/Table/Setting/Attribute";
import AttributeModal from "../../../components/UI/Modal/AttributeModal";

const CustomerAttributeSetting = () => {
  const { t } = useTranslation();
  const [deletingId, setDeletingId] = useState<number>();
  let [searchParams, setSearchParams] = useSearchParams();

  const id = searchParams.get("id");

  const setSearchParamsHandler = (key: string, value: string) => {
    searchParams.set(key, value);
    setSearchParams(searchParams);
  };

  const removeSearchParamsHandler = (key: string) => {
    searchParams.delete(key);
    setSearchParams(searchParams);
  };

  const { modal, openModalHandler, closeModalHandler } = useModal(
    undefined,
    () => removeSearchParamsHandler("id")
  );

  const { enqueueSnackbar } = useSnackbar();

  const breadcrumbs: IBreadcrumbsAndMenu[] = [
    {
      name: t("setting.index"),
      to: "/setting",
    },
    {
      name: t("setting.contact.index"),
      to: "/setting/contact",
    },
    {
      name: t("setting.contact.customer_attribute"),
    },
  ];

  const graphQLClientGeneral: GraphQLClient =
    createGraphQLClientWithMiddleware("general");

  const { data: rowData, refetch } =
    useCustomerAttributesFindAllQuery<CustomerAttributesFindAllQuery>(
      graphQLClientGeneral
    );

  const { data, isLoading, isSuccess } =
    useCustomerAttributeFindUniqueQuery<CustomerAttributeFindUniqueQuery>(
      graphQLClientGeneral,
      {
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
      },
      {
        enabled: Boolean(id),
      }
    );

  const { mutateAsync: createCustomerAttribute } =
    useCustomerAttributeCreateMutation<Error>(graphQLClientGeneral);

  const { mutateAsync: updateCustomerAttribute } =
    useCustomerAttributeUpdateMutation<Error>(graphQLClientGeneral);

  const { mutateAsync: deleteCustomerAttribute } =
    useCustomerAttributeDeleteMutation<Error>(graphQLClientGeneral);

  const onAddHandler = async (data: IAttribute) => {
    try {
      await createCustomerAttribute({
        data,
      });
      enqueueSnackbar(`เพิ่ม${t("setting.contact.customer_attribute")}สำเร็จ`, {
        variant: "success",
      });
      await refetch();
    } catch (err) {
      enqueueSnackbar(
        `เพิ่ม${t("setting.contact.customer_attribute")}ไม่สำเร็จ`,
        {
          variant: "error",
        }
      );
    }
  };

  const onEditHandler = (id?: number) => {
    if (id) {
      setSearchParamsHandler("id", id.toString());
      openModalHandler();
    }
  };

  const onUpdateHandler = async (data: IAttribute) => {
    try {
      const { id, ...otherData } = data;
      await updateCustomerAttribute({
        uniqueInput: {
          id: id,
        },
        data: otherData,
      });
      enqueueSnackbar(`แก้ไข${t("setting.contact.customer_attribute")}สำเร็จ`, {
        variant: "success",
      });
      await refetch();
    } catch (err) {
      enqueueSnackbar(
        `แก้ไข${t("setting.contact.customer_attribute")}ไม่สำเร็จ`,
        {
          variant: "error",
        }
      );
    }
  };

  const onDeleteHandler = (id?: number) => {
    if (id) {
      setDeletingId(id);
      openConfirmationHandler();
    }
  };

  const deleteActionHandler = async () => {
    try {
      await deleteCustomerAttribute({
        uniqueInput: {
          id: deletingId,
        },
      });
      setDeletingId(undefined);
      enqueueSnackbar(`ลบ${t("setting.contact.customer_attribute")}สำเร็จ`, {
        variant: "success",
      });
      await refetch();
    } catch (err) {
      enqueueSnackbar(`ลบ${t("setting.contact.customer_attribute")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const {
    modal: confirmation,
    openModalHandler: openConfirmationHandler,
    closeModalHandler: closeConfirmationHandler,
    submitModalHandler: submitConfirmationHandler,
  } = useModal(deleteActionHandler);

  return (
    <>
      <CustomizedBreadcrumbs breadcrumbs={breadcrumbs} />
      <Box sx={{ display: "flex", justifyContent: "space-between", my: 3 }}>
        <Typography variant="h5">
          {t("setting.contact.customer_attribute")}
        </Typography>
        <CustomizedButton
          title={t("button.add") + t("setting.contact.customer_attribute")}
          variant="contained"
          onClick={openModalHandler}
        />
      </Box>
      <AttributeTable
        data={rowData?.CustomerAttributesFindAll || []}
        onEditHandler={onEditHandler}
        onDeleteHandler={onDeleteHandler}
      />
      <AttributeModal
        open={modal}
        closeModalHandler={closeModalHandler}
        onAddHandler={onAddHandler}
        onUpdateHandler={onUpdateHandler}
        data={data?.CustomerAttributeFindUnique}
        isLoading={isLoading}
        isSuccess={isSuccess}
      />
      <ConfirmationModal
        title={`ยืนยันการลบ${t("setting.contact.customer_attribute")}`}
        open={confirmation}
        handleClose={closeConfirmationHandler}
        action={submitConfirmationHandler}
      />
    </>
  );
};

export default CustomerAttributeSetting;
